import useUser from "@/hooks/useUser";
import { resetReservationGraceTimeMilliseconds } from "@shared/constants";
import { assignBy } from "mt-shared/collections";
import { useCallback, useMemo } from "react";
import { ReservationsContext } from "./ReservationsContext";
import useInitialReservationsOnUserChange from "./useInitialReservationsOnUserChange";
import usePendingReservations from "./usePendingReservations";
import useReservationActions from "./useReservationActions";
import useReservationBounds from "./useReservationBounds";
import useReservationFetch from "./useReservationFetch";
import useReservationSearchState from "./useReservationSearchState";
import useReservationsBuckets from "./useReservationsBuckets";
import useReservationsCache from "./useReservationsCache";
import useReservationsQueries from "./useReservationsQueries";
import useReservationsState from "./useReservationsState";

/**
 * Provides reservations data and actions to descendant components.
 */
export default function ReservationsProvider({
  children,
}: React.PropsWithChildren) {
  const {
    user: { userId },
  } = useUser();

  const {
    from,
    setFrom,
    until,
    setUntil,
    venueId,
    setVenueId,
    productId,
    setProductId,
    setAppliedVenueId,
    setAppliedProductId,
    appliedSearchVariables,
    hasSearchParams,
    clearSearchState,
  } = useReservationSearchState();

  const {
    reservations,
    totalReservations,
    reservationsStart,
    reservationsEnd,
    reachedPastEnd,
    reachedUpcomingEnd,
    reservationBounds,
    fetchedMonthsRange,
    applyBucketState,
  } = useReservationsState();

  const canFetchMoreUpcomingReservations = !reachedUpcomingEnd;
  const canFetchMorePastReservations = !reachedPastEnd;

  const defaultVariables = useMemo(
    () => ({
      venuePortalUserId: userId!,
      gracePeriodMs: resetReservationGraceTimeMilliseconds,
    }),
    [userId],
  );

  const {
    fetchReservationsQuery,
    data,
    error,
    loading,
    fetchReservationBoundsQuery,
    fetchReservation,
    fetchReservationResult,
  } = useReservationsQueries();

  const { clearReservationsCache } = useReservationsCache(userId);

  const {
    activeBucketKeyRef,
    getOrCreateBucket,
    resetBucket,
    updateBucket,
    bucketKey,
  } = useReservationsBuckets({
    appliedSearchVariables,
    applyBucketState,
    clearReservationsCache,
  });

  useReservationBounds({
    userId,
    bucketKey,
    appliedSearchVariables,
    fetchReservationBoundsQuery,
    updateBucket,
  });

  const {
    fetchReservations,
    fetchMorePastReservations,
    fetchMoreUpcomingReservations,
    fetchInitialReservations,
  } = useReservationFetch({
    userId,
    from,
    until,
    reservations,
    fetchedMonthsRange,
    appliedSearchVariables,
    defaultVariables,
    canFetchMorePastReservations,
    canFetchMoreUpcomingReservations,
    fetchReservationsQuery,
    getOrCreateBucket,
    updateBucket,
    resetBucket,
    activeBucketKeyRef,
    setAppliedVenueId,
    setAppliedProductId,
  });

  useInitialReservationsOnUserChange(userId, fetchInitialReservations);

  const {
    pendingReservations,
    pendingReservationsCount,
    setPendingReservations,
  } = usePendingReservations({
    data,
    gracePeriodMs: resetReservationGraceTimeMilliseconds,
  });

  const {
    editVenueReservationAnswerMutationResponse,
    acceptReservation,
    rejectReservation,
    resetReservation,
  } = useReservationActions({
    userId,
    updateBucket,
    activeBucketKeyRef,
    setPendingReservations,
  });

  /**
   * Clears search filters and refetches the default range.
   */
  const resetSearch = useCallback(async () => {
    if (!hasSearchParams) return;
    clearSearchState();
    await fetchInitialReservations({}, new Date());
  }, [clearSearchState, fetchInitialReservations, hasSearchParams]);

  const reservationsById = useMemo(
    () => assignBy([...reservations, ...pendingReservations], "id"),
    [reservations, pendingReservations],
  );

  return (
    <ReservationsContext.Provider
      value={{
        acceptReservation,
        canFetchMorePastReservations,
        canFetchMoreUpcomingReservations,
        editVenueReservationAnswerMutationResponse,
        error,
        fetchedMonthsRange,
        fetchInitialReservations,
        fetchMorePastReservations,
        fetchMoreUpcomingReservations,
        fetchReservation,
        fetchReservationResult,
        fetchReservations,
        from,
        hasSearchParams,
        loading,
        pendingReservations,
        pendingReservationsCount,
        productId,
        rejectReservation,
        reservationBounds,
        reservations,
        reservationsById,
        reservationsEnd,
        reservationsStart,
        resetReservation,
        resetSearch,
        setFrom,
        setProductId,
        setUntil,
        setVenueId,
        totalReservations,
        until,
        venueId,
      }}
    >
      {children}
    </ReservationsContext.Provider>
  );
}
